// The Policy tab's renderer input (arc 101 slice 06) — a resolved policy
// snapshot turned into printable sections.
//
// GENERIC BY CONSTRUCTION, and deliberately NOT schema-aware. `GET
// /v1/repos/:o/:r/policy` answers the snapshot AFTER `PolicySchema.parse`, so
// every section the schema declares is already present with its defaults
// applied. Walking the snapshot's own keys therefore covers all ~15 sections —
// and keeps covering the next one for free — without the SPA importing
// `@developerz/policy` (zod + the yaml parser, into a browser bundle) or
// restating a key list that drifts the moment a field lands. The renderer this
// replaced hand-wrote three sections (`triage` / `pr` / `escalate`) and RENAMED
// their keys, so eleven sections were invisible (`handoff`, `release`, `digest`,
// `limits`, `coding`, `ci`, `mcp_servers`, `integrations`, `kb`, `review`,
// `tone`) and the three it showed didn't match the file a maintainer edits.
//
// Values print in `.maintainer.yml`'s own vocabulary — the tab's whole job is to
// answer "what did my file resolve to", and a renamed key can't be looked up in
// the file. Key ORDER is the snapshot's own (zod emits shape order, i.e. schema
// order); nothing depends on it, since coverage comes from iterating, not from a
// list — a reordered schema shuffles panels, it never hides one.

/** One panel's worth of resolved policy: its `.maintainer.yml` key + body text. */
export type PolicySection = { readonly key: string; readonly text: string };

/**
 * `escalate` is excluded from the generic grid: the dedicated escalation matrix
 * renders the same two tiers tone-coded, which is strictly more readable than
 * `always: [security]`. Rendering both would print it twice.
 */
const MATRIX_KEY = 'escalate';

/** Synthetic key for the panel that groups the snapshot's top-level scalars. */
export const POLICY_ROOT_KEY = '__root__';

const INDENT = '  ';

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isScalar = (v: unknown): boolean => !isRecord(v) && !Array.isArray(v);

/**
 * One scalar in YAML spelling. An empty string prints as `''` and a missing
 * value as `null` — both are resolved states worth seeing, and a blank line
 * would read as a rendering bug. Total by design: an unmodelled shape falls back
 * to JSON rather than `[object Object]`.
 */
const scalar = (v: unknown): string => {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'string') return v.length > 0 ? v : "''";
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v) ?? 'null';
};

/** `[a, b]` — a scalar list stays on one line, the way it reads in the file. */
const inlineList = (xs: readonly unknown[]): string => `[${xs.map(scalar).join(', ')}]`;

/** `key: value`, or `key:` + an indented block for anything nested. */
function entryLines(key: string, value: unknown, indent: string): readonly string[] {
  if (Array.isArray(value)) {
    if (value.length === 0) return [`${indent}${key}: []`];
    if (value.every(isScalar)) return [`${indent}${key}: ${inlineList(value)}`];
    return [`${indent}${key}:`, ...value.flatMap((item) => itemLines(item, indent + INDENT))];
  }
  if (isRecord(value)) {
    const nested = Object.entries(value);
    if (nested.length === 0) return [`${indent}${key}: {}`];
    return [`${indent}${key}:`, ...nested.flatMap(([k, v]) => entryLines(k, v, indent + INDENT))];
  }
  return [`${indent}${key}: ${scalar(value)}`];
}

/** One element of a block list — `- ` on its first line, the rest aligned under. */
function itemLines(item: unknown, indent: string): readonly string[] {
  if (Array.isArray(item)) return [`${indent}- ${inlineList(item)}`];
  if (!isRecord(item)) return [`${indent}- ${scalar(item)}`];
  const lines = Object.entries(item).flatMap(([k, v]) => entryLines(k, v, indent));
  const [first = `${indent}{}`, ...rest] = lines;
  return [`${indent}- ${first.slice(indent.length)}`, ...rest.map((l) => INDENT + l)];
}

/** One section's body: a record/array block, or a bare scalar. */
export function formatPolicySection(value: unknown): string {
  if (Array.isArray(value)) {
    return value.length === 0 ? '[]' : value.flatMap((i) => itemLines(i, '')).join('\n');
  }
  if (isRecord(value)) {
    const entries = Object.entries(value);
    return entries.length === 0
      ? '{}'
      : entries.flatMap(([k, v]) => entryLines(k, v, '')).join('\n');
  }
  return scalar(value);
}

/**
 * Every renderable section of the snapshot, in its own key order. Top-level
 * scalars (`version`, `schema`, `format_command`) are grouped into one leading
 * section instead of a panel each — `format_command` is a real knob and used to
 * be invisible, but three one-line panels would be noise.
 */
export function policySections(
  snapshot: Readonly<Record<string, unknown>>,
): readonly PolicySection[] {
  const root: string[] = [];
  const sections: PolicySection[] = [];
  for (const [key, value] of Object.entries(snapshot)) {
    if (key === MATRIX_KEY) continue;
    if (isScalar(value)) root.push(...entryLines(key, value, ''));
    else sections.push({ key, text: formatPolicySection(value) });
  }
  return root.length === 0
    ? sections
    : [{ key: POLICY_ROOT_KEY, text: root.join('\n') }, ...sections];
}

/** The two escalation tiers, defensively — the snapshot is untyped JSON. */
export function escalationTiers(snapshot: Readonly<Record<string, unknown>>): {
  readonly always: string[];
  readonly ask: string[];
} {
  const esc = isRecord(snapshot[MATRIX_KEY]) ? snapshot[MATRIX_KEY] : {};
  const strings = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  return { always: strings(esc['always']), ask: strings(esc['ask_before_acting']) };
}
